import { TextInput, ActionIcon, Box, Select, MultiSelect } from '@mantine/core'
import { IconTrash } from '@tabler/icons-react'
import { useArcTranslation } from 'hooks'
import type { StudentRowProps } from './interfaces'
import { GRID_TEMPLATE, GRID_TEMPLATE_NO_REMOVE, GRID_GAP } from './columns'

const naturalSort = (a: string, b: string) =>
  a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })

export function StudentRow({
  student,
  index,
  onChange,
  onGroupIdsChange,
  grades,
  groups,
  showRemove = false,
  onRemove,
  onCreateGroup,
}: StudentRowProps) {
  const { t } = useArcTranslation()

  const sortedGroups = [
    { value: '__create__', label: `+ ${t('student_requests.create_group')}` },
    ...[...groups].sort((a, b) => naturalSort(a.label, b.label)),
  ]
  const sortedGrades = [...grades].sort((a, b) => naturalSort(a.label, b.label))

  const handleGroupChange = (values: string[]) => {
    if (values.includes('__create__')) {
      onCreateGroup()
      return
    }
    onGroupIdsChange(index, values)
  }

  return (
    <Box
      px={2}
      sx={{
        display: 'grid',
        gridTemplateColumns: showRemove ? GRID_TEMPLATE : GRID_TEMPLATE_NO_REMOVE,
        gap: GRID_GAP,
        alignItems: 'start',
      }}
    >
      <TextInput
        size='sm'
        placeholder={t('add_students_modal.first_name_placeholder')}
        value={student.firstName}
        onChange={e => onChange(index, 'firstName', e.target.value)}
        error={student.errors?.firstName}
      />
      <TextInput
        size='sm'
        placeholder={t('add_students_modal.last_name_placeholder')}
        value={student.lastName}
        onChange={e => onChange(index, 'lastName', e.target.value)}
        error={student.errors?.lastName}
      />
      <TextInput
        size='sm'
        placeholder={t('add_students_modal.username_auto')}
        value={student.username}
        onChange={e => onChange(index, 'username', e.target.value)}
        styles={{ input: { fontFamily: 'monospace', fontSize: 13 } }}
        error={student.errors?.username}
      />
      <MultiSelect
        size='sm'
        placeholder={t('add_students_modal.groups_placeholder')}
        data={sortedGroups}
        value={student.groupIds}
        onChange={handleGroupChange}
        withinPortal
        searchable
        error={student.errors?.groupIds}
      />
      <Select
        size='sm'
        placeholder={t('add_students_modal.grade_placeholder')}
        data={sortedGrades}
        value={student.gradeId}
        onChange={v => onChange(index, 'gradeId', v ?? '')}
        clearable
        withinPortal
        error={student.errors?.gradeId}
      />
      {showRemove && (
        <ActionIcon variant='light' color='red' size='sm' onClick={onRemove} mt={4}>
          <IconTrash size={16} />
        </ActionIcon>
      )}
    </Box>
  )
}
